import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getFriendRequests, acceptFriendRequest, rejectFriendRequest } from '../services/api';

/**
 * Incoming friend requests, in the header.
 *
 * NO POLLING — this codebase has none anywhere, and a bell is not worth
 * introducing the pattern. Follows AdminPanel's convention instead: fetch once
 * on mount, then mutate locally after each action. It refetches when the panel
 * is opened, so the count is at most one interaction stale.
 */
export default function FriendRequestsBell() {
  const [requests, setRequests] = useState([]);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const panelRef = useRef(null);
  const buttonRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await getFriendRequests({ direction: 'incoming' });
      setRequests(res?.requests || []);
    } catch (_) {
      // Silent: a bell that can't load is not worth an error banner in the header.
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Click-outside + Escape to dismiss.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target) || buttonRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const respond = async (friendshipId, action) => {
    setBusyId(friendshipId);
    setError('');
    try {
      if (action === 'accept') await acceptFriendRequest(friendshipId);
      else await rejectFriendRequest(friendshipId);
      // Drop it locally rather than refetching — same optimistic pattern the
      // admin Requests tab uses.
      setRequests(prev => prev.filter(r => r.friendship_id !== friendshipId));
    } catch (err) {
      setError(err.message || 'Could not respond');
    } finally {
      setBusyId(null);
    }
  };

  const count = requests.length;

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => { const next = !open; setOpen(next); if (next) load(); }}
        aria-label={count ? `${count} friend request${count === 1 ? '' : 's'}` : 'Friend requests'}
        aria-expanded={open}
        className="relative flex items-center justify-center transition-opacity hover:opacity-80"
        style={{
          // Matches the sibling theme toggle exactly — same padding, radius,
          // border and fill — so the header reads as one control group.
          padding: '5px 8px',
          borderRadius: 8,
          border: '1px solid var(--border-accent)',
          background: 'var(--accent-dim)',
          color: 'var(--text-muted)',
          cursor: 'pointer',
          lineHeight: 1.4,
        }}
      >
        <span aria-hidden="true" style={{ fontSize: 14 }}>🔔</span>
        {count > 0 && (
          <span
            className="absolute flex items-center justify-center font-bold"
            style={{
              top: -5, right: -5, minWidth: 15, height: 15, padding: '0 4px',
              fontSize: 9, borderRadius: 4,
              background: 'var(--color-danger)', color: 'var(--on-primary)',
            }}
          >
            {count}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          className="absolute right-0 mt-2 rounded-xl overflow-hidden z-50"
          style={{
            width: 288,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-strong)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
          }}
        >
          <div className="px-3 py-2.5" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
            <p
              className="font-mono text-[10px] font-medium uppercase tracking-[0.2em]"
              style={{ color: 'var(--text-faint)' }}
            >
              Friend requests
            </p>
          </div>

          {error && (
            <p
              className="px-3 py-2 text-xs"
              style={{ background: 'var(--color-danger-dim)', color: 'var(--color-danger)' }}
            >
              {error}
            </p>
          )}

          {count === 0 ? (
            <p className="px-3 py-4 text-xs text-center" style={{ color: 'var(--text-faint)' }}>
              No pending requests
            </p>
          ) : (
            <ul className="max-h-80 overflow-y-auto">
              {requests.map((r) => (
                <li
                  key={r.friendship_id}
                  className="px-3 py-2.5 flex items-center gap-2"
                  style={{ borderBottom: '1px solid var(--border-subtle)' }}
                >
                  <p className="text-xs flex-1 min-w-0" style={{ color: 'var(--text-primary)' }}>
                    <span className="font-semibold">{r.member_name}</span>
                    <span style={{ color: 'var(--text-muted)' }}> wants to be your friend</span>
                  </p>
                  <button
                    type="button"
                    disabled={busyId === r.friendship_id}
                    onClick={() => respond(r.friendship_id, 'accept')}
                    className="text-xs font-semibold px-2 py-1 rounded-md flex-shrink-0 transition-opacity hover:opacity-80 disabled:opacity-50"
                    style={{
                      background: 'var(--color-success-dim)',
                      border: '1px solid var(--color-success-border)',
                      color: 'var(--color-success)',
                    }}
                  >
                    Accept
                  </button>
                  <button
                    type="button"
                    disabled={busyId === r.friendship_id}
                    onClick={() => respond(r.friendship_id, 'reject')}
                    className="text-xs font-semibold px-2 py-1 rounded-md flex-shrink-0 transition-opacity hover:opacity-80 disabled:opacity-50"
                    style={{
                      background: 'var(--bg-input)',
                      border: '1px solid var(--border-subtle)',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    Reject
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
